import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  InputNumber,
  Typography,
  Space,
  Table,
  Spin,
  Statistic,
  Tabs,
  Progress,
  Tag
} from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { useTranslation } from 'react-i18next';
import Page from '../components/layout/Page';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Modern color palette
const COLORS = {
  primary: '#2C4B39',
  secondary: '#D4E0C9',
  accent1: '#2C4B39',
  accent2: '#2C4B39',
  accent3: '#D4E0C9',
  accent4: '#D4A5A5',
  background: '#F9F9F9',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
};

const PIE_COLORS = ['#2C4B39', '#D4E0C9', '#D4A5A5', '#A5D4D4', '#D4D4A5'];

// Month names in German
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

// Demo data generators
const generateLoanTypeData = () => [
  { name: 'Bauspardarlehen', value: 65 },
  { name: 'Annitätendarlehen', value: 20 },
  { name: 'Privatkredit', value: 10 },
  { name: 'Zwischenfinanzierung', value: 5 },
];

const generatePoolerData = () => [
  { name: 'Europace', loans: 15, amount: 4500000, commission: 55000 },
  { name: 'Forum', loans: 10, amount: 3200000, commission: 42000 },
  { name: 'Direkteinreicher', loans: 8, amount: 2800000, commission: 35000 }
];

const generateCustomerData = () => [
  { name: 'Familie Müller', loans: 3, amount: 850000, commission: 12000, city: 'Köln' },
  { name: 'Dr. Schmidt', loans: 2, amount: 620000, commission: 8500, city: 'Düsseldorf' },
  { name: 'Herr Weber', loans: 2, amount: 580000, commission: 7800, city: 'Leverkusen' },
  { name: 'Familie Becker', loans: 1, amount: 450000, commission: 6200, city: 'Bonn' },
  { name: 'Frau Klein', loans: 1, amount: 380000, commission: 5100, city: 'Köln' },
];

const generateReferrerData = () => [
  { name: 'Immobilien Schmidt', company: 'Schmidt & Partner GmbH', loans: 8, amount: 2400000, commission: 32000 },
  { name: 'Makler Meyer', company: 'Immobilien Meyer', loans: 7, amount: 2100000, commission: 28000 },
  { name: 'Haus & Grund', company: 'Haus & Grund Köln', loans: 6, amount: 1800000, commission: 24000 },
  { name: 'Immocenter', company: 'Immocenter GmbH', loans: 5, amount: 1500000, commission: 20000 },
  { name: 'Schulze Immobilien', company: 'Schulze & Co KG', loans: 4, amount: 1200000, commission: 16000 },
  { name: 'Immo Experten', company: 'IE GmbH', loans: 4, amount: 1200000, commission: 16000 },
  { name: 'Haus Vermittlung', company: 'HV AG', loans: 3, amount: 900000, commission: 12000 },
  { name: 'Wagner Immobilien', company: 'Wagner Group', loans: 3, amount: 900000, commission: 12000 },
  { name: 'Immobilien Plus', company: 'IP GmbH', loans: 2, amount: 600000, commission: 8000 },
  { name: 'Beste Immobilien', company: 'BI KG', loans: 2, amount: 600000, commission: 8000 },
];

// Formatter functions
const formatEuro = (value) => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatPercent = (value) => {
  if (value === undefined || value === null) return '-';
  return `${Number(value).toFixed(1)}%`;
};

const formatDays = (value) => {
  if (value === undefined || value === null) return '-';
  return `${value} ${value === 1 ? 'Tag' : 'Tage'}`;
};

const MyAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dashboardData, setDashboardData] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [bankData, setBankData] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch dashboard data
        const dashboardResponse = await fetch(`/api/dashboard_data/?year=${year}`);
        const dashboardResult = await dashboardResponse.json();
        setDashboardData(dashboardResult);

        // Fetch monthly data
        const monthlyResponse = await fetch(`/api/monthly_commission_data/?year=${year}`);
        const monthlyResult = await monthlyResponse.json();
        setMonthlyData(monthlyResult.map(item => ({
          ...item,
          monthName: MONTHS[item.month - 1]
        })));

        // Fetch bank data
        const bankResponse = await fetch(`/api/bank_analytics/?year=${year}`);
        const bankResult = await bankResponse.json();
        setBankData(bankResult);

      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [year]);

  // Calculate YTD totals from real data
  const ytdTotalAmount = dashboardData?.total_amount || 0;
  const ytdTotalLoans = dashboardData?.total_loans || 0;
  const ytdTotalCommission = dashboardData?.total_commission || 0;
  const ytdAvgAmount = ytdTotalLoans ? ytdTotalAmount / ytdTotalLoans : 0;
  const ytdAvgTimeToClose = dashboardData?.avg_time_to_close || 0;

  // Real KPIs
  const withdrawalRate = dashboardData?.withdrawal_rate || 0;
  const conversionRate = dashboardData?.conversion_rate || 0;
  const yearlyGoal = 30000000; // This could come from user settings
  const goalProgress = Math.round((ytdTotalAmount / yearlyGoal) * 100);

  const renderKPICards = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title={t('my_analytics.kpis.totalAmount')}
            value={ytdTotalAmount}
            formatter={(value) => formatEuro(value)}
            suffix={`(${year})`}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title={t('my_analytics.kpis.totalLoans')}
            value={ytdTotalLoans}
            suffix={`(${year})`}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title={t('my_analytics.kpis.avgAmount')}
            value={ytdAvgAmount}
            formatter={(value) => formatEuro(value)}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title={t('my_analytics.kpis.totalCommission')}
            value={ytdTotalCommission}
            formatter={(value) => formatEuro(value)}
            suffix={`(${year})`}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title={t('my_analytics.kpis.withdrawalRate')}
            value={withdrawalRate}
            suffix="%"
            valueStyle={{ color: withdrawalRate > 5 ? COLORS.error : COLORS.success }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title={t('my_analytics.kpis.conversionRate')}
            value={conversionRate}
            suffix="%"
            valueStyle={{ color: conversionRate < 50 ? COLORS.warning : COLORS.success }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title={t('my_analytics.kpis.avgTimeToClose')}
            value={ytdAvgTimeToClose}
            formatter={(value) => formatDays(value)}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <div style={{ textAlign: 'center' }}>
            <p>{t('my_analytics.kpis.yearlyGoalProgress')}</p>
            <Progress type="circle" percent={goalProgress} size={100} />
          </div>
        </Card>
      </Col>
    </Row>
  );

  const renderPerformanceCharts = () => {
    const items = [
      {
        key: 'amount',
        label: t('my_analytics.charts.loanAmount'),
        children: (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="monthName" 
                interval={0}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis />
              <Tooltip formatter={(value) => formatEuro(value)} />
              <Legend />
              <Line
                type="monotone"
                dataKey="evaluation_amount"
                name={t('my_analytics.charts.totalAmount')}
                stroke={COLORS.primary}
              />
            </LineChart>
          </ResponsiveContainer>
        ),
      },
      {
        key: 'commission',
        label: t('my_analytics.charts.commission'),
        children: (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="monthName"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis />
              <Tooltip formatter={(value) => formatEuro(value)} />
              <Legend />
              <Bar
                dataKey="commission"
                name={t('my_analytics.charts.commission')}
                fill={COLORS.primary}
              />
            </BarChart>
          </ResponsiveContainer>
        ),
      },
    ];

    return (
      <Card title={t('my_analytics.charts.performance')}>
        <Tabs items={items} />
      </Card>
    );
  };

  const renderLoanTypeDistribution = () => (
    <Card title={t('my_analytics.charts.loanTypeDistribution')}>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={generateLoanTypeData()}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={100}
            label={(entry) => `${entry.name} (${entry.value}%)`}
          >
            {generateLoanTypeData().map((_, index) => (
              <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => `${value}%`} />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  );

  const renderBankTable = () => (
    <Card title={t('my_analytics.tables.bankPerformance')}>
      <Table
        size="small"
        dataSource={bankData}
        rowKey="bank_name"
        columns={[
          {
            title: t('my_analytics.tables.columns.bank'),
            dataIndex: 'bank_name',
            key: 'bank_name',
          },
          {
            title: t('my_analytics.tables.columns.loans'),
            dataIndex: 'loan_count',
            key: 'loan_count',
            align: 'right',
          },
          {
            title: t('my_analytics.tables.columns.amount'),
            dataIndex: 'total_amount',
            key: 'total_amount',
            align: 'right',
            render: (value) => formatEuro(value),
          },
          {
            title: t('my_analytics.tables.columns.commission'),
            dataIndex: 'total_commission',
            key: 'total_commission',
            align: 'right',
            render: (value) => formatEuro(value),
          },
        ]}
      />
    </Card>
  );

  const renderPoolerTable = () => (
    <Card title={t('my_analytics.tables.poolerPerformance')}>
      <Table
        size="small"
        dataSource={generatePoolerData()}
        rowKey="name"
        columns={[
          {
            title: t('my_analytics.tables.columns.pooler'),
            dataIndex: 'name',
            key: 'name',
          },
          {
            title: t('my_analytics.tables.columns.loans'),
            dataIndex: 'loans',
            key: 'loans',
            align: 'right',
          },
          {
            title: t('my_analytics.tables.columns.amount'),
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (value) => formatEuro(value),
          },
          {
            title: t('my_analytics.tables.columns.commission'),
            dataIndex: 'commission',
            key: 'commission',
            align: 'right',
            render: (value) => formatEuro(value),
          },
        ]}
      />
    </Card>
  );

  const renderCustomerTable = () => (
    <Card title={t('my_analytics.tables.customerPerformance')}>
      <Table
        size="small"
        dataSource={generateCustomerData()}
        rowKey="name"
        columns={[
          {
            title: t('my_analytics.tables.columns.customer'),
            dataIndex: 'name',
            key: 'name',
          },
          {
            title: t('my_analytics.tables.columns.city'),
            dataIndex: 'city',
            key: 'city',
          },
          {
            title: t('my_analytics.tables.columns.loans'),
            dataIndex: 'loans',
            key: 'loans',
            align: 'right',
          },
          {
            title: t('my_analytics.tables.columns.amount'),
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (value) => formatEuro(value),
          },
          {
            title: t('my_analytics.tables.columns.commission'),
            dataIndex: 'commission',
            key: 'commission',
            align: 'right',
            render: (value) => formatEuro(value),
          },
        ]}
      />
    </Card>
  );

  const renderReferrerTable = () => (
    <Card title={t('my_analytics.tables.referrerPerformance')}>
      <Table
        size="small"
        dataSource={generateReferrerData()}
        rowKey="name"
        pagination={false}
        columns={[
          {
            title: t('my_analytics.tables.columns.referrer'),
            render: (_, record) => (
              <div>
                <div>{record.name}</div>
                <small style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{record.company}</small>
              </div>
            ),
          },
          {
            title: t('my_analytics.tables.columns.loans'),
            dataIndex: 'loans',
            align: 'right',
            width: 80,
          },
          {
            title: t('my_analytics.tables.columns.amount'),
            dataIndex: 'amount',
            align: 'right',
            width: 120,
            render: (value) => formatEuro(value),
          },
        ]}
      />
    </Card>
  );

  return (
    <Page
      title={t('my_analytics.title')}
      subtitle={t('my_analytics.subtitle')}
    >
      <Spin spinning={loading}>
        {/* Year Filter */}
        <Card style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>{t('my_analytics.filters.year')}</Text>
            <InputNumber
              style={{ width: 200 }}
              value={year}
              onChange={(value) => setYear(value)}
            />
          </Space>
        </Card>

        {/* KPI Summary */}
        {renderKPICards()}

        {/* Performance Charts & Top Referrers */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={18}>
            {renderPerformanceCharts()}
          </Col>
          <Col xs={24} lg={6}>
            {renderReferrerTable()}
          </Col>
        </Row>

        {/* Loan Type Distribution & Customer Overview */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            {renderLoanTypeDistribution()}
          </Col>
          <Col xs={24} lg={12}>
            {renderCustomerTable()}
          </Col>
        </Row>

        {/* Bank & Pooler Performance */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            {renderBankTable()}
          </Col>
          <Col xs={24} lg={12}>
            {renderPoolerTable()}
          </Col>
        </Row>
      </Spin>
    </Page>
  );
};

export default MyAnalytics;
